import React, {useEffect, useState} from 'react';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {customerName, formatTerm, todayIso} from './inquiryUtils';

/** Společné prvky modálních oken v detailu požadavku. */
export const ModalActions = ({onCancel, submitLabel, submitting = false, danger = false, disabled = false}) => (
    <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50">
            Zpět
        </button>
        <button
            type="submit"
            disabled={submitting || disabled}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
        >
            {submitting ? 'Ukládám…' : submitLabel}
        </button>
    </div>
);

export const Checkbox = ({checked, onChange, children, hint}) => (
    <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={checked} onChange={(event) => onChange(event.target.checked)}/>
        <span>
            {children}
            {hint ? <span className="block text-xs text-gray-500">{hint}</span> : null}
        </span>
    </label>
);

export const TextArea = ({label, value, onChange, rows = 4, placeholder, autoFocus = false}) => (
    <label className="block text-sm">
        {label ? <span className="mb-1 block font-medium">{label}</span> : null}
        <textarea
            autoFocus={autoFocus}
            rows={rows}
            value={value}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
    </label>
);

/**
 * Změna stavu s volitelnou zprávou a e-mailem zákazníkovi (potvrzení / zrušení rezervace…).
 * `submit({message, notify})` vrací Promise; při chybě zůstane okno otevřené.
 */
export const StatusModal = ({close, description, submitLabel, notifyLabel, notifyDefault = true, messageLabel, messagePlaceholder, danger, submit}) => {
    const [message, setMessage] = useState('');
    const [notify, setNotify] = useState(notifyDefault);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        const ok = await submit({message, notify: Boolean(notifyLabel) && notify});
        setSubmitting(false);
        if (ok) close(true);
    };

    return (
        <form onSubmit={onSubmit} className="w-[min(32rem,90vw)] space-y-4">
            {description ? <p className="text-sm text-gray-700">{description}</p> : null}
            {notifyLabel ? <Checkbox checked={notify} onChange={setNotify}>{notifyLabel}</Checkbox> : null}
            {messageLabel && (!notifyLabel || notify) ? (
                <TextArea label={messageLabel} value={message} onChange={setMessage} rows={3} placeholder={messagePlaceholder}/>
            ) : null}
            <ModalActions onCancel={() => close(false)} submitLabel={submitLabel} submitting={submitting} danger={danger}/>
        </form>
    );
};

/** Výběr nového termínu podle obsazenosti v kalendáři. */
export const RescheduleModal = ({close, inquiry, submit}) => {
    const reservation = inquiry.reservation || {};
    const [date, setDate] = useState(reservation.date >= todayIso() ? reservation.date : todayIso());
    const [time, setTime] = useState(reservation.time || '');
    const [day, setDay] = useState(null);
    const [confirm, setConfirm] = useState(true);
    const [notify, setNotify] = useState(true);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setDay(null);
        API.get(API_ENDPOINTS.webInquiry.calendar, {params: {from: date, days: 1}, meta: {silentError: true}})
            .then(({data}) => {
                if (!cancelled) setDay(data?.days?.[0] || null);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [date]);

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!date || !time) return;
        setSubmitting(true);
        const ok = await submit({date, time, confirm, notify, message});
        setSubmitting(false);
        if (ok) close(true);
    };

    const sameAsNow = date === reservation.date && time === reservation.time;

    return (
        <form onSubmit={onSubmit} className="w-[min(36rem,90vw)] space-y-4">
            <p className="text-sm text-gray-700">
                Současný termín: <strong>{formatTerm(reservation.date, reservation.time, {long: true})}</strong>
                {reservation.minutes ? <> · náročnost {reservation.minutes} min</> : null}
            </p>
            <label className="block text-sm">
                <span className="mb-1 block font-medium">Den</span>
                <input
                    type="date"
                    value={date}
                    min={todayIso()}
                    onChange={(event) => {
                        setDate(event.target.value);
                        setTime('');
                    }}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
            </label>
            {day ? (
                day.closed ? (
                    <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">V tento den je zavřeno{day.note ? ` (${day.note})` : ''}. Vyberte jiný den.</p>
                ) : (
                    <div>
                        <p className="mb-2 text-xs text-gray-600">
                            Na servisu: {day.technicians} · obsazeno {day.used} z {day.capacity} min
                            {day.note ? ` · ${day.note}` : ''}
                        </p>
                        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
                            {day.slots.map((slot) => {
                                const full = slot.taken >= day.technicians;
                                const selected = slot.time === time;
                                return (
                                    <button
                                        key={slot.time}
                                        type="button"
                                        onClick={() => setTime(slot.time)}
                                        title={full ? 'V tento čas už je plno' : slot.taken ? `Obsazeno ${slot.taken} z ${day.technicians}` : 'Volno'}
                                        className={`rounded border px-2 py-1.5 text-sm ${selected ? 'border-indigo-600 bg-indigo-600 text-white' : full ? 'border-red-200 bg-red-50 text-red-400 line-through' : slot.taken ? 'border-amber-300 bg-amber-50' : 'border-gray-300 bg-white hover:border-indigo-400'}`}
                                    >
                                        {slot.time}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )
            ) : (
                <p className="text-sm text-gray-500">Načítám obsazenost…</p>
            )}
            <div className="space-y-2">
                <Checkbox checked={confirm} onChange={setConfirm} hint="Termín se zablokuje v kalendáři.">Termín rovnou potvrdit</Checkbox>
                <Checkbox checked={notify} onChange={setNotify}>Poslat zákazníkovi e-mail s novým termínem</Checkbox>
            </div>
            {notify ? <TextArea label="Zpráva pro zákazníka (nepovinné)" value={message} onChange={setMessage} rows={2}/> : null}
            <ModalActions onCancel={() => close(false)} submitLabel="Uložit termín" submitting={submitting} disabled={!time || (sameAsNow && !confirm)}/>
        </form>
    );
};

/** Odpověď zákazníkovi e-mailem. */
export const ReplyModal = ({close, inquiry, defaultSubject, submit, offerOption = false}) => {
    const [subject, setSubject] = useState(defaultSubject);
    const [text, setText] = useState(`Dobrý den${inquiry.contact?.name ? `, ${inquiry.contact.name.split(' ')[0]}` : ''},\n\n\n\nS pozdravem\nAppleLab`);
    const [markOffer, setMarkOffer] = useState(offerOption);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        const ok = await submit({subject, text, markOffer: offerOption && markOffer});
        setSubmitting(false);
        if (ok) close(true);
    };

    return (
        <form onSubmit={onSubmit} className="w-[min(40rem,90vw)] space-y-3">
            <p className="text-sm text-gray-600">Komu: <strong>{inquiry.contact?.email}</strong></p>
            <label className="block text-sm">
                <span className="mb-1 block font-medium">Předmět</span>
                <input
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
            </label>
            <TextArea label="Text" value={text} onChange={setText} rows={10} autoFocus/>
            {offerOption ? <Checkbox checked={markOffer} onChange={setMarkOffer}>Označit jako „Nabídka odeslána“</Checkbox> : null}
            <ModalActions onCancel={() => close(false)} submitLabel="Odeslat e-mail" submitting={submitting} disabled={!text.trim()}/>
        </form>
    );
};

/** Propojení požadavku se zákazníkem: nalezení shody podle e-mailu / telefonu, nebo založení nového. */
export const CustomerModal = ({close, inquiry, submit}) => {
    const [matches, setMatches] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        API.get(API_ENDPOINTS.webInquiry.customerMatches(inquiry._id), {meta: {silentError: true}})
            .then(({data}) => setMatches(data || []))
            .catch(() => setMatches([]));
    }, [inquiry._id]);

    const choose = async (payload) => {
        setSubmitting(true);
        const ok = await submit(payload);
        setSubmitting(false);
        if (ok) close(true);
    };

    return (
        <div className="w-[min(36rem,90vw)] space-y-4">
            <p className="text-sm text-gray-700">
                Údaje z webu: <strong>{inquiry.contact?.company || inquiry.contact?.name}</strong>
                {[inquiry.contact?.email, inquiry.contact?.phone].filter(Boolean).map((value) => ` · ${value}`)}
            </p>
            {matches === null ? (
                <p className="text-sm text-gray-500">Hledám existující zákazníky…</p>
            ) : matches.length ? (
                <div>
                    <p className="mb-2 text-sm font-medium">Nalezení zákazníci se stejným kontaktem</p>
                    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                        {matches.map((customer) => (
                            <li key={customer._id} className="flex items-center justify-between gap-3 p-3">
                                <span className="min-w-0 text-sm">
                                    <span className="block font-medium">{customerName(customer) || 'Bez jména'}</span>
                                    <span className="block truncate text-xs text-gray-500">
                                        {[customer.emails?.[0]?.value, customer.phones?.[0]?.value].filter(Boolean).join(' · ')}
                                        {customer.matchedBy?.length ? ` · shoda: ${customer.matchedBy.map((item) => (item === 'email' ? 'e-mail' : 'telefon')).join(', ')}` : ''}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => choose({customerId: customer._id})}
                                    className="shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    Použít
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">Zákazník se stejným e-mailem ani telefonem zatím v ERP není.</p>
            )}
            <div className="flex justify-between gap-2 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => close(false)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50">
                    Zpět
                </button>
                <button
                    type="button"
                    disabled={submitting}
                    onClick={() => choose({create: true})}
                    className="rounded-md border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                >
                    Založit nového zákazníka z údajů
                </button>
            </div>
        </div>
    );
};
